import { css, SerializedStyles } from '@emotion/react';
import { ColorValue, ProgressBarRadius, ProgressBarSize, useHoneycombTheme } from '../../theming';
import { alpha, isHoneycombRadius, isProgressBarSize } from '../../utils';

const getRadiusValue = (value: 'rounded' | string) => {
    return value === 'rounded' ? `calc(var(--progress-bar-size) / 2)` : value;
};

// Static styles
export const PROGRESS_BAR_STATIC_STYLES: SerializedStyles = css`
    --progress-bar-radius: var(--hc-radius-xs);
    --progress-bar-color: var(--hc-color-primary-base);
    --progress-bar-background: var(--hc-color-neutral-tint-100);

    --progress-bar-size-xs: 16px;
    --progress-bar-size-s: 20px;
    --progress-bar-size-m: 24px;
    --progress-bar-size-l: 32px;
    --progress-bar-size-xl: 48px;

    --progress-bar-size: var(--progress-bar-size-m);

    position: relative;
    height: var(--progress-bar-size);
    border-radius: var(--progress-bar-radius);
    overflow: hidden;
    display: flex;

    background-color: var(--progress-bar-background);

    [data-progress-bar-inner] {
        background-color: var(--progress-bar-color);
        height: 100%;
        width: var(--progress-bar-complete-width);
        display: flex;
        align-items: center;
        justify-content: flex-end;
        overflow: hidden;
        transition: width 100ms ease, background-color 200ms ease;

        &:where(:last-of-type) {
            border-radius: 0;
            border-start-end-radius: var(--progress-bar-radius);
            border-end-end-radius: var(--progress-bar-radius);
        }

        &:where(:first-of-type) {
            border-radius: 0;
            border-start-start-radius: var(--progress-bar-radius);
            border-end-start-radius: var(--progress-bar-radius);
        }
    }

    [data-progress-bar-label] {
        color: var(--hc-color-common-white);
        font-weight: bold;
        user-select: none;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        line-height: 1;
        padding-inline: calc(var(--hc-spacing-unit) * 4);
    }
`;

export function useProgressBarCssVariables(
    value: number,
    color: ColorValue,
    size: ProgressBarSize | string,
    radius: ProgressBarRadius | string
): React.CSSProperties {
    const { theme } = useHoneycombTheme();
    const resolvedColor = theme.fn.resolveColorValue(color);

    return {
        '--progress-bar-complete-width': `${value}%`,
        '--progress-bar-color': resolvedColor,
        '--progress-bar-background': alpha(resolvedColor, 0.2),
        '--progress-bar-size': isProgressBarSize(size) ? `var(--progress-bar-size-${size})` : size,
        '--progress-bar-radius': isHoneycombRadius(radius) ? `var(--hc-radius-${radius})` : getRadiusValue(radius),
    } as React.CSSProperties;
}
